import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Resets the scroll position when the route changes.
 *
 * A browser does this for free on a full page load, but a react-router
 * navigation only swaps the rendered tree and leaves the document scrolled where
 * it was. Following a link from halfway down the feed therefore dropped you
 * halfway down the next page. React Router ships `<ScrollRestoration>` for this,
 * but only for data routers; this app mounts a plain `<BrowserRouter>`, so the
 * behaviour has to be supplied here.
 *
 * Two deliberate exceptions:
 *
 *   POP   is back and forward, including the first render of a session. The
 *         browser restores the previous offset itself on those, and overriding
 *         it would throw away the position the reader deliberately left.
 *   hash  is a request for a specific place in the document, so the top is the
 *         one position it does not want.
 *
 * `useLayoutEffect` rather than `useEffect`: it runs after the new route has
 * been committed but before the browser paints, so the new page is never shown
 * at the old offset first.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (navigationType === "POP" || hash) return;
    // "instant" is explicit so this keeps working if `scroll-behavior: smooth`
    // is ever added to the root; animating a whole page on navigation reads as
    // lag rather than polish.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash, navigationType]);

  return null;
};

export default ScrollToTop;
